import JMuxerModule from "jmuxer/dist/jmuxer.min.js";
import { performanceStore } from "../performance/store";
import { directControl } from "./controlStore";
import type { DirectTouch } from "./protobuf";

interface Callbacks {
  onFrame: (frame: VideoFrame) => void;
  onState: (state: string) => void;
  onError: (error: Error) => void;
}

interface SessionResponse {
  connectMode: "direct" | "relay";
  stream: { url: string; token: string };
  control: { url: string; token: string };
}

interface JMuxerInstance { feed: (data: { video: Uint8Array; duration?: number; isLastVideoFrameComplete?: boolean }) => void; destroy: () => void; }
interface JMuxerOptions {
  node: HTMLVideoElement; mode: "video"; flushingTime: number; maxDelay: number;
  clearBuffer: boolean; fps: number; onReady: () => void;
  onError: (error: unknown) => void; onUnsupportedCodec: (codec: string) => void;
}
type JMuxerConstructor = new (options: JMuxerOptions) => JMuxerInstance;

const HEADER_SIZE = 28;
const TYPE = { HELLO: 1, CONFIG: 2, FRAME: 3, METRICS: 4, RESTART: 6, KEYFRAME: 7 };
const keyCodes: Record<string, number> = {
  GoHome: 3, GoBack: 4, Power: 26, AppSwitch: 187,
  AudioVolumeUp: 24, AudioVolumeDown: 25,
  Enter: 66, Backspace: 67, Delete: 112, Escape: 4, Tab: 61, " ": 62,
  ArrowUp: 19, ArrowDown: 20, ArrowLeft: 21, ArrowRight: 22,
};

const androidKeyCode = (key: string) => {
  if (keyCodes[key] !== undefined) return keyCodes[key];
  if (/^[a-z]$/i.test(key)) return 29 + key.toUpperCase().charCodeAt(0) - 65;
  if (/^[0-9]$/.test(key)) return 7 + Number(key);
  return undefined;
};
const setU16 = (view: DataView, offset: number, value: number) => view.setUint16(offset, value, false);
const setU32 = (view: DataView, offset: number, value: number) => view.setUint32(offset, value, false);
const concat = (left: Uint8Array | undefined, right: Uint8Array) => {
  if (!left?.byteLength) return right.slice();
  const result = new Uint8Array(left.byteLength + right.byteLength);
  result.set(left); result.set(right, left.byteLength);
  return result;
};
const websocketUrl = (value: string) => value.startsWith("/")
  ? `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}${value}`
  : value;

export class RemoteWaydroidClient {
  static webCodecsSupported() { return "VideoDecoder" in window && "EncodedVideoChunk" in window; }
  static mseSupported() { return "MediaSource" in window; }
  static supported() { return this.webCodecsSupported() || this.mseSupported(); }

  private videoSocket?: WebSocket;
  private controlSocket?: WebSocket;
  private decoder?: VideoDecoder;
  private muxer?: JMuxerInstance;
  private codecConfig?: Uint8Array;
  private waitingForKeyframe = true;
  private configured = false;
  private stopped = false;
  private generation = 0;
  private reconnectTimer?: number;
  private activeTouches = new Set<number>();
  private decoded = 0;
  private received = 0;
  private dropped = 0;
  private intervalFrames = 0;
  private intervalStarted = performance.now();
  private decodeTotalMs = 0;
  private submitted = new Map<number, number>();
  private lastFrameAt?: number;
  private maxFreezeMs = 0;
  private controlMessages = 0;
  private controlBytes = 0;
  private lastMseTimestamp?: number;
  private msePlaying = false;
  private videoFrameCallbackId?: number;
  private lastPresentedFrames = 0;
  private mseLiveTimer?: number;

  constructor(
    private readonly callbacks: Callbacks,
    private readonly androidWidth: number,
    private readonly androidHeight: number,
    private readonly video?: HTMLVideoElement,
  ) {}

  connect() {
    this.stopped = false;
    this.callbacks.onState("connecting");
    performanceStore.update({ backend: "remote-waydroid", connectionState: "connecting" });
    void this.open(++this.generation);
  }

  disconnect() {
    this.stopped = true;
    this.generation += 1;
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.closeSockets();
    this.closeDecoder();
    this.destroyMse();
    this.callbacks.onState("disconnected");
  }

  reconnect() {
    this.closeSockets();
    this.closeDecoder();
    this.destroyMse();
    this.codecConfig = undefined;
    this.waitingForKeyframe = true;
    this.configured = false;
    this.connect();
  }

  setProfile(_profile: unknown) { /* The Pi profile is configured by its systemd environment. */ }

  private async open(generation: number) {
    if (!RemoteWaydroidClient.supported()) {
      this.callbacks.onError(new Error("This browser cannot decode the Pi H.264 stream"));
      return;
    }
    try {
      const sessionId = crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`;
      const response = await fetch("/api/remote-waydroid/sessions", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, origin: window.location.origin, ttlSeconds: 90 }),
      });
      if (!response.ok) throw new Error(`session request failed (${response.status})`);
      const session = await response.json() as SessionResponse;
      if (this.stopped || generation !== this.generation) return;
      if (!RemoteWaydroidClient.webCodecsSupported()) {
        if (!this.video) throw new Error("The HTTP H.264 player is unavailable");
        this.initializeMse(this.video);
      }
      this.openStream(session);
      this.openControl(session);
      performanceStore.update({ connectionMode: session.connectMode });
    } catch (error) {
      if (this.stopped || generation !== this.generation) return;
      this.callbacks.onError(error instanceof Error ? error : new Error(String(error)));
      this.scheduleReconnect();
    }
  }

  private openStream(session: SessionResponse) {
    const socket = new WebSocket(websocketUrl(session.stream.url), ["wdr1", `is4f.${session.stream.token}`]);
    socket.binaryType = "arraybuffer";
    this.videoSocket = socket;
    socket.onopen = () => this.requestKeyframe();
    socket.onmessage = (event) => this.receive(event.data as ArrayBuffer);
    socket.onerror = () => this.callbacks.onError(new Error("Pi WDR1 stream connection failed"));
    socket.onclose = () => this.scheduleReconnect();
  }

  private openControl(session: SessionResponse) {
    const socket = new WebSocket(websocketUrl(session.control.url), ["scrcpy-4.1", `is4f.${session.control.token}`]);
    socket.binaryType = "arraybuffer";
    this.controlSocket = socket;
    socket.onopen = () => performanceStore.update({ dataChannelState: "open" });
    socket.onclose = () => performanceStore.update({ dataChannelState: "closed" });
    // A 409 while AI owns control is expected and must not interrupt video.
    socket.onerror = () => performanceStore.update({ dataChannelState: "unavailable" });
  }

  private closeSockets() {
    for (const socket of [this.videoSocket, this.controlSocket]) {
      if (socket) { socket.onclose = null; socket.close(); }
    }
    this.videoSocket = this.controlSocket = undefined;
  }

  private scheduleReconnect() {
    if (this.stopped || this.reconnectTimer) return;
    performanceStore.update({
      connectionState: "reconnecting",
      reconnectCount: performanceStore.getSnapshot().reconnectCount + 1,
    });
    this.callbacks.onState("reconnecting");
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined;
      this.reconnect();
    }, 1000);
  }

  private receive(buffer: ArrayBuffer) {
    try {
      if (buffer.byteLength < HEADER_SIZE) throw new Error("short WDR1 packet");
      const bytes = new Uint8Array(buffer);
      if (String.fromCharCode(...bytes.slice(0, 4)) !== "WDR1" || bytes[4] !== 1) throw new Error("invalid WDR1 packet");
      const view = new DataView(buffer);
      const type = view.getUint8(5);
      const flags = view.getUint8(6);
      const timestamp = Number(view.getBigUint64(12, false));
      const width = view.getUint16(20, false);
      const height = view.getUint16(22, false);
      const size = view.getUint32(24, false);
      if (size !== buffer.byteLength - HEADER_SIZE) throw new Error("invalid WDR1 payload length");
      const payload = bytes.slice(HEADER_SIZE);
      if (type === TYPE.HELLO) {
        const hello = JSON.parse(new TextDecoder().decode(payload));
        performanceStore.update({
          backend: "remote-waydroid", senderCodec: hello.codec,
          senderEncoder: "bcm2835-codec-h264", senderEncoderType: "hardware",
          configuredFps: 30, width: width || 960, height: height || 540,
          icePath: "Pi WDR1 H.264 WebSocket", protocol: "tcp",
        });
      } else if (type === TYPE.CONFIG) {
        this.codecConfig = payload;
        if (RemoteWaydroidClient.webCodecsSupported()) void this.configureDecoder();
      } else if (type === TYPE.FRAME) {
        this.receiveFrame(timestamp, payload, Boolean(flags & 1));
      } else if (type === TYPE.METRICS) {
        const metrics = JSON.parse(new TextDecoder().decode(payload));
        performanceStore.update({
          capturedFps: metrics.capture?.average_captured_fps,
          encodedFps: metrics.capture?.average_encoded_fps || metrics.encoded_fps,
          streamQueueDepth: metrics.queue_depth,
          streamGatewayDrops: metrics.gateway_drops,
          bitrateMbps: metrics.bitrate_bps === undefined ? undefined : Math.round(metrics.bitrate_bps / 10_000) / 100,
        });
      } else if (type === TYPE.RESTART) {
        this.waitingForKeyframe = true;
        this.configured = false;
        this.closeDecoder();
        this.requestKeyframe();
      }
    } catch (error) {
      this.callbacks.onError(error instanceof Error ? error : new Error(String(error)));
    }
  }

  private receiveFrame(timestamp: number, payload: Uint8Array, key: boolean) {
    this.received += 1;
    if (this.waitingForKeyframe && !key) { this.dropped += 1; this.updateStats(); return; }
    if (!RemoteWaydroidClient.webCodecsSupported()) { this.feedMse(timestamp, payload, key); return; }
    if (!this.configured || !this.decoder || this.decoder.state !== "configured") {
      if (key) void this.configureDecoder().then(() => this.decode(timestamp, payload, true));
      return;
    }
    this.decode(timestamp, payload, key);
  }

  private async configureDecoder() {
    if (this.configured) return;
    if (!this.decoder) {
      this.decoder = new VideoDecoder({
        output: (frame) => {
          const now = performance.now();
          if (this.lastFrameAt) this.maxFreezeMs = Math.max(this.maxFreezeMs, now - this.lastFrameAt);
          this.lastFrameAt = now;
          const submitted = this.submitted.get(frame.timestamp);
          if (submitted !== undefined) { this.decodeTotalMs += now - submitted; this.submitted.delete(frame.timestamp); }
          this.decoded += 1; this.intervalFrames += 1;
          this.callbacks.onFrame(frame); this.updateStats();
          if (this.decoded === 1) {
            performanceStore.update({ connectionState: "connected" });
            this.callbacks.onState("streaming");
          }
        },
        error: (error) => { this.waitingForKeyframe = true; this.configured = false; this.callbacks.onError(error); },
      });
    }
    const config: VideoDecoderConfig = {
      codec: "avc1.42E01F", optimizeForLatency: true, hardwareAcceleration: "no-preference",
    };
    const support = await VideoDecoder.isConfigSupported(config);
    if (!support.supported) throw new Error("Browser cannot decode constrained-baseline H.264");
    this.decoder.configure(config);
    this.configured = true;
    performanceStore.update({ decoderImplementation: "WebCodecs · browser-selected H.264 decoder" });
  }

  private decode(timestamp: number, payload: Uint8Array, key: boolean) {
    if (!this.decoder || this.decoder.state !== "configured") return;
    if (this.decoder.decodeQueueSize > 2 && !key) { this.dropped += 1; this.updateStats(); return; }
    if (key) this.waitingForKeyframe = false;
    this.submitted.set(timestamp, performance.now());
    try {
      this.decoder.decode(new EncodedVideoChunk({
        type: key ? "key" : "delta", timestamp, data: key ? concat(this.codecConfig, payload) : payload,
      }));
    } catch (error) {
      this.dropped += 1; this.waitingForKeyframe = true; this.requestKeyframe();
      this.callbacks.onError(error instanceof Error ? error : new Error(String(error)));
    }
  }

  private closeDecoder() {
    if (this.decoder && this.decoder.state !== "closed") this.decoder.close();
    this.decoder = undefined; this.configured = false; this.submitted.clear();
  }

  private initializeMse(video: HTMLVideoElement) {
    const JMuxer = JMuxerModule as JMuxerConstructor | undefined;
    if (typeof JMuxer !== "function") throw new Error("Media Source H.264 player failed to load");
    video.muted = true;
    video.addEventListener("canplay", this.playMse);
    video.addEventListener("playing", this.markMseStreaming);
    this.muxer = new JMuxer({
      node: video, mode: "video", flushingTime: 0, maxDelay: 80, clearBuffer: true, fps: 30,
      onReady: this.playMse,
      onError: (error) => this.callbacks.onError(error instanceof Error ? error : new Error(String(error))),
      onUnsupportedCodec: (codec) => this.callbacks.onError(new Error(`Browser cannot decode ${codec}`)),
    });
    performanceStore.update({ decoderImplementation: "Media Source Extensions · native H.264" });
    this.mseLiveTimer = window.setInterval(this.keepMseAtLiveEdge, 200);
    if ("requestVideoFrameCallback" in video) this.videoFrameCallbackId = video.requestVideoFrameCallback(this.trackMseFrame);
  }

  private readonly playMse = () => { void this.video?.play().catch((error) => this.callbacks.onError(new Error(String(error)))); };
  private readonly markMseStreaming = () => {
    performanceStore.update({ connectionState: "connected" });
    if (!this.msePlaying) { this.msePlaying = true; this.callbacks.onState("streaming"); }
  };
  private readonly trackMseFrame: VideoFrameRequestCallback = (_now, metadata) => {
    const presented = metadata.presentedFrames || this.lastPresentedFrames + 1;
    const increment = Math.max(1, presented - this.lastPresentedFrames);
    this.lastPresentedFrames = presented; this.decoded += increment; this.intervalFrames += increment;
    const quality = this.video?.getVideoPlaybackQuality?.();
    if (quality) this.dropped = Math.max(this.dropped, quality.droppedVideoFrames);
    const now = performance.now();
    if (this.lastFrameAt) this.maxFreezeMs = Math.max(this.maxFreezeMs, now - this.lastFrameAt);
    this.lastFrameAt = now; this.updateStats(); this.markMseStreaming();
    if (!this.stopped && this.video && "requestVideoFrameCallback" in this.video) this.videoFrameCallbackId = this.video.requestVideoFrameCallback(this.trackMseFrame);
  };

  private feedMse(timestamp: number, payload: Uint8Array, key: boolean) {
    if (!this.muxer) return;
    if (key) this.waitingForKeyframe = false;
    const previous = this.lastMseTimestamp; this.lastMseTimestamp = timestamp;
    this.muxer.feed({
      video: key ? concat(this.codecConfig, payload) : payload,
      duration: previous === undefined ? 33 : Math.max(1, Math.min(250, Math.round((timestamp - previous) / 1000))),
      isLastVideoFrameComplete: true,
    });
  }

  private readonly keepMseAtLiveEdge = () => {
    const video = this.video;
    if (!video?.buffered.length) return;
    const liveEdge = video.buffered.end(video.buffered.length - 1);
    const lag = liveEdge - video.currentTime;
    // Plain LAN HTTP cannot expose WebCodecs, so MSE is the standards-based
    // fallback. MSE may otherwise preserve an old playback queue while direct
    // controls continue to affect Android. Drop that stale queue aggressively;
    // this screen is interactive live media, not a recording.
    if (lag > 0.35) video.currentTime = Math.max(0, liveEdge - 0.05);
    video.playbackRate = lag > 0.12 ? 1.08 : 1;
  };

  private destroyMse() {
    this.muxer?.destroy(); this.muxer = undefined; this.msePlaying = false;
    if (this.mseLiveTimer !== undefined) window.clearInterval(this.mseLiveTimer);
    this.mseLiveTimer = undefined;
    if (this.video) {
      this.video.removeEventListener("canplay", this.playMse);
      this.video.removeEventListener("playing", this.markMseStreaming);
      if (this.videoFrameCallbackId !== undefined && "cancelVideoFrameCallback" in this.video) this.video.cancelVideoFrameCallback(this.videoFrameCallbackId);
      this.video.pause(); this.video.playbackRate = 1; this.video.removeAttribute("src"); this.video.load();
    }
    this.videoFrameCallbackId = undefined; this.lastMseTimestamp = undefined; this.lastPresentedFrames = 0;
  }

  private updateStats() {
    const now = performance.now(); const elapsed = now - this.intervalStarted;
    const total = this.decoded + this.dropped;
    const values: Parameters<typeof performanceStore.update>[0] = {
      framesReceived: this.received, framesDecoded: this.decoded, framesDropped: this.dropped,
      droppedFramePercent: total ? Math.round(this.dropped / total * 10_000) / 100 : 0,
      averageDecodeTimeMs: this.decoded ? Math.round(this.decodeTotalMs / this.decoded * 100) / 100 : 0,
      decoderQueueSize: this.decoder?.decodeQueueSize || 0, maxFreezeDurationMs: Math.round(this.maxFreezeMs * 10) / 10,
    };
    if (elapsed >= 1000) { values.fps = Math.round(this.intervalFrames * 1000 / elapsed * 10) / 10; this.intervalFrames = 0; this.intervalStarted = now; }
    performanceStore.update(values);
  }

  private requestKeyframe() {
    if (!this.videoSocket || this.videoSocket.readyState !== WebSocket.OPEN) return;
    const packet = new ArrayBuffer(HEADER_SIZE); const bytes = new Uint8Array(packet);
    bytes.set([87, 68, 82, 49, 1, TYPE.KEYFRAME]); this.videoSocket.send(packet);
  }

  sendTouch(touches: DirectTouch[], essential = false) {
    for (const touch of touches) {
      const wasDown = this.activeTouches.has(touch.identifier); const down = touch.pressure > 0;
      this.sendTouchPacket(touch.identifier, touch.x, touch.y, down ? (wasDown ? 2 : 0) : 1, down ? touch.pressure / 0x7fff : 0, essential);
      if (down) this.activeTouches.add(touch.identifier); else this.activeTouches.delete(touch.identifier);
    }
  }

  private sendTouchPacket(id: number, x: number, y: number, action: number, pressure: number, essential: boolean) {
    const packet = new ArrayBuffer(32); const view = new DataView(packet);
    view.setUint8(0, 2); view.setUint8(1, action); view.setBigUint64(2, BigInt(id), false);
    setU32(view, 10, x); setU32(view, 14, y); setU16(view, 18, this.androidWidth); setU16(view, 20, this.androidHeight);
    setU16(view, 22, Math.round(Math.max(0, Math.min(1, pressure)) * 65535)); setU32(view, 24, 0); setU32(view, 28, 0);
    this.sendControl(packet, essential);
  }

  sendKey(key: string, eventType: 0 | 1 | 2 = 2) {
    if (!directControl.canHumanControl()) return;
    const code = androidKeyCode(key); if (code === undefined) return;
    if (eventType === 2) { this.sendKeyPacket(code, 0); this.sendKeyPacket(code, 1); }
    else this.sendKeyPacket(code, eventType);
  }
  private sendKeyPacket(code: number, action: number) {
    const packet = new ArrayBuffer(14); const view = new DataView(packet);
    view.setUint8(0, 0); view.setUint8(1, action); setU32(view, 2, code); setU32(view, 6, 0); setU32(view, 10, 0);
    this.sendControl(packet, true);
  }

  sendScroll(x: number, y: number, deltaX: number, deltaY: number) {
    if (!directControl.canHumanControl()) return;
    const packet = new ArrayBuffer(21); const view = new DataView(packet);
    view.setUint8(0, 3); setU32(view, 1, x); setU32(view, 5, y); setU16(view, 9, this.androidWidth); setU16(view, 11, this.androidHeight);
    view.setInt16(13, deltaX === 0 ? 0 : deltaX > 0 ? -32767 : 32767, false);
    view.setInt16(15, deltaY === 0 ? 0 : deltaY > 0 ? -32767 : 32767, false); setU32(view, 17, 0);
    this.sendControl(packet, true);
  }

  rotate() { const packet = new ArrayBuffer(1); new DataView(packet).setUint8(0, 11); this.sendControl(packet, true); }

  private sendControl(packet: ArrayBuffer, essential: boolean) {
    if (!directControl.canHumanControl()) return;
    const socket = this.controlSocket;
    if (!socket || socket.readyState !== WebSocket.OPEN || (!essential && socket.bufferedAmount > 32 * 1024)) return;
    socket.send(packet); this.controlMessages += 1; this.controlBytes += packet.byteLength;
    performanceStore.update({ dataMessagesSent: this.controlMessages, dataBytesSent: this.controlBytes, dataBufferedAmount: socket.bufferedAmount });
  }
}
